import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  message,
  Space,
  Divider
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
// 添加 react-quill 导入
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // 引入样式

const { Option } = Select;
const { RangePicker } = DatePicker;

const NoticeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const quillRef = React.useRef(null);

  useEffect(() => {
    if (id) {
      fetchNoticeDetail();
    }
  }, [id]);

  const fetchNoticeDetail = async () => {
    try {
      const response = await api.get(`/admin/notices/${id}`);
      if (response.code === 200) {
        const notice = response.data;
        // 使用dayjs处理日期
        const startTime = notice.startTime ? dayjs(notice.startTime) : null;
        const endTime = notice.endTime ? dayjs(notice.endTime) : null;
        
        form.setFieldsValue({
          title: notice.title,
          content: notice.content,
          linkUrl: notice.linkUrl,
          linkType: notice.linkType?.toString(),
          targetPage: notice.targetPage,
          sortOrder: notice.sortOrder,
          status: notice.status?.toString(),
          type: notice.type?.toString(),
          timeRange: startTime && endTime ? [startTime, endTime] : null,
        });
      } else {
        message.error(response.message || '获取公告详情失败');
      }
    } catch (error) {
      message.error('获取公告详情失败');
    }
  };

  // 自定义图片上传处理
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      
      // 创建 FormData 对象用于上传图片
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'general'); // 指定文件类型
      
      try {
        // 调用真实的图片上传接口
        const response = await api.post('/common/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.code === 200 && response.data && response.data.fileUrl) {
          // 获取 Quill 实例
          const quill = quillRef.current.getEditor();
          // 插入图片到编辑器
          const range = quill.getSelection();
          quill.insertEmbed(range.index, 'image', response.data.fileUrl);
        } else {
          message.error(response.message || '图片上传失败');
        }
      } catch (error) {
        message.error('图片上传失败: ' + (error.message || '未知错误'));
      }
    };
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // 处理时间范围
      const params = {
        ...values,
        startTime: values.timeRange ? values.timeRange[0].format('YYYY-MM-DD HH:mm:ss') : null,
        endTime: values.timeRange ? values.timeRange[1].format('YYYY-MM-DD HH:mm:ss') : null,
        linkType: parseInt(values.linkType),
        status: parseInt(values.status),
        type: parseInt(values.type),
        sortOrder: parseInt(values.sortOrder),
      };
      
      // 删除timeRange字段
      delete params.timeRange;
      
      let response;
      if (id) {
        // 更新
        response = await api.put(`/admin/notices/${id}`, params);
      } else {
        // 创建
        response = await api.post('/admin/notices', params);
      }
      
      if (response.code === 200) {
        message.success(id ? '更新成功' : '创建成功');
        navigate('/notices');
      } else {
        message.error(response.message || (id ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      message.error(error.message || (id ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };

  // 配置 react-quill 的 toolbar
  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <Card 
      title={id ? '编辑公告' : '新建公告'}
      extra={
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/notices')}
        >
          返回列表
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          linkType: '1',
          sortOrder: 0,
          status: '1',
          type: '1',
        }}
      >
        <Form.Item
          label="标题"
          name="title"
          rules={[{ required: true, message: '请输入标题' }]}
        >
          <Input placeholder="请输入公告标题" />
        </Form.Item>

        <Form.Item
          label="内容（富文本）"
          name="content"
          rules={[{ required: true, message: '请输入公告内容' }]}
        >
          <ReactQuill 
            ref={quillRef}
            theme="snow" 
            modules={modules}
            formats={formats}
            placeholder="请输入公告内容..."
            style={{ height: '200px' }}
          />
        </Form.Item>
        {/* 添加一个空的 div 来调整布局，因为 react-quill 的高度设置 */}
        <div style={{ height: '40px' }}></div>

        <Divider>公告类型</Divider>

        <Form.Item
          label="公告类型"
          name="type"
          rules={[{ required: true, message: '请选择公告类型' }]}
        >
          <Select>
            <Option value="1">系统公告</Option>
            <Option value="2">营销公告</Option>
          </Select>
        </Form.Item>

        <Divider>链接设置（可选）</Divider>

        <Form.Item
          label="链接地址"
          name="linkUrl"
        >
          <Input placeholder="请输入链接地址，如：https://example.com" />
        </Form.Item>

        <Form.Item
          label="链接类型"
          name="linkType"
        >
          <Select>
            <Option value="1">内部页面</Option>
            <Option value="2">外部链接</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="内部页面标识"
          name="targetPage"
        >
          <Input placeholder="当链接类型为内部页面时，填写页面标识" />
        </Form.Item>

        <Divider>其他设置</Divider>

        <Form.Item
          label="排序"
          name="sortOrder"
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入排序值" />
        </Form.Item>

        <Form.Item
          label="有效期"
          name="timeRange"
        >
          <RangePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="状态"
          name="status"
          valuePropName="checked"
        >
          <Switch checkedChildren="上架" unCheckedChildren="下架" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/notices')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default NoticeForm;